/**
 * Data cleaning page.
 *
 * Full flow:
 *   1. Read the Excel selection (values + metadata)
 *   2. Choose one or more cleaning operations
 *   3. Preview the cleaning result (before/after) with write strategy
 *   4. Confirm via ActionPreviewPanel inside CleaningPreviewCard → apply
 *
 * All Office.js calls go through the office/ barrel.
 * The cleaning logic lives in the backend service.
 * Operation metadata is sourced from shared/constants (single source of truth).
 */

import React, { useState, useCallback } from 'react';
import {
  Button,
  Checkbox,
  Text,
  MessageBar,
  MessageBarBody,
  Badge,
} from '@fluentui/react-components';
import {
  BroomRegular,
  DocumentSearchRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';
import type {
  CleaningOperation,
  CleaningOperationSummary,
} from '@excel-ai-studio/shared';
import { CLEANING_OPERATIONS_META } from '@excel-ai-studio/shared';
import { cleanData } from '../services/api.client';
import {
  readSelectionInfo,
  writeValues,
  writeValuesAdjacent,
  type SelectionInfo,
} from '../office';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorDisplay } from '../components/common/ErrorDisplay';
import { ConfidenceBadge } from '../components/common/ConfidenceBadge';
import { CleaningPreviewCard } from '../components/features/CleaningPreviewCard';
import { getSettings } from '../store/settings.store';
import { addHistoryEntry } from '../store/history.store';

// ─── Constants ───────────────────────────────────────────

/** Maximum cells we accept without a warning */
const LARGE_SELECTION_THRESHOLD = 5000;

type WriteStrategy = 'in_place' | 'adjacent';

/** Operations that may empty-out cells (extract pattern not found → "") */
const EXTRACT_OPS: ReadonlySet<CleaningOperation> = new Set([
  'extract_emails',
  'extract_numbers',
  'extract_dates',
]);

// ─── Component ───────────────────────────────────────────

export const CleaningPage: React.FC = () => {
  // Selection state
  const [selectionInfo, setSelectionInfo] = useState<SelectionInfo>();
  const [selectionLoading, setSelectionLoading] = useState(false);

  // Operation state
  const [selectedOps, setSelectedOps] = useState<Set<CleaningOperation>>(new Set());

  // Preview state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<{
    operationSummaries: readonly CleaningOperationSummary[];
    changesCount: number;
    cleanedValues: unknown[][];
    warnings: readonly string[];
  }>();

  // Write state
  const [applied, setApplied] = useState(false);
  const [appliedStrategy, setAppliedStrategy] = useState<WriteStrategy>('adjacent');

  // Shared state
  const [error, setError] = useState<string>();

  // ─── Helpers ─────────────────────────────────────────

  const resetPreview = () => {
    setPreviewResult(undefined);
    setApplied(false);
  };

  const resetAll = () => {
    setSelectionInfo(undefined);
    setSelectedOps(new Set());
    resetPreview();
    setError(undefined);
  };

  const isSelectionEmpty = (values: unknown[][]): boolean =>
    values.every((row) =>
      row.every((cell) => cell === null || cell === undefined || String(cell).trim() === ''),
    );

  const cellCount = selectionInfo
    ? selectionInfo.rowCount * selectionInfo.colCount
    : 0;

  // ─── Computed warnings for the operations panel ──────

  const operationWarnings: string[] = [];

  if (selectionInfo) {
    // Dedup on single-row: useless
    if (selectedOps.has('remove_duplicates') && selectionInfo.rowCount === 1) {
      operationWarnings.push(
        'Suppression de doublons : la selection ne contient qu\'une seule ligne. L\'operation n\'aura aucun effet.',
      );
    }

    // Dedup on multi-column: clarify scope
    if (selectedOps.has('remove_duplicates') && selectionInfo.colCount > 1 && selectionInfo.rowCount > 1) {
      operationWarnings.push(
        `Suppression de doublons : les lignes entieres (${selectionInfo.colCount} colonnes) sont comparees. Deux lignes ne sont identiques que si toutes leurs cellules correspondent.`,
      );
    }

    // Extract ops on single-cell: probably not useful
    const hasExtract = [...selectedOps].some((op) => EXTRACT_OPS.has(op));
    if (hasExtract && selectionInfo.rowCount === 1 && selectionInfo.colCount === 1) {
      operationWarnings.push(
        'Extraction sur une seule cellule. Selectionnez une plage plus grande pour un nettoyage pertinent.',
      );
    }

    // Large selection
    if (cellCount > LARGE_SELECTION_THRESHOLD) {
      operationWarnings.push(
        `Selection volumineuse (${cellCount.toLocaleString('fr-FR')} cellules). L'apercu sera partiel.`,
      );
    }
  }

  // ─── Step 1: Read selection ──────────────────────────

  const handleReadSelection = async () => {
    setError(undefined);
    resetPreview();
    setSelectionLoading(true);

    try {
      const info = await readSelectionInfo();

      if (!info.values || info.values.length === 0) {
        setError('Aucune cellule selectionnee. Selectionnez des donnees dans Excel puis reessayez.');
        setSelectionInfo(undefined);
        return;
      }

      if (isSelectionEmpty(info.values)) {
        setError('La selection ne contient que des cellules vides. Selectionnez des donnees puis reessayez.');
        setSelectionInfo(undefined);
        return;
      }

      setSelectionInfo(info);
    } catch (err) {
      setError((err as Error).message);
      setSelectionInfo(undefined);
    } finally {
      setSelectionLoading(false);
    }
  };

  // ─── Step 2: Toggle operations ───────────────────────

  const toggleOp = (op: CleaningOperation) => {
    resetPreview();
    setSelectedOps((prev) => {
      const next = new Set(prev);
      if (next.has(op)) next.delete(op);
      else next.add(op);
      return next;
    });
  };

  // ─── Step 3: Preview cleaning ────────────────────────

  const handlePreview = async () => {
    if (!selectionInfo || selectedOps.size === 0) return;
    const startedAt = Date.now();
    const settings = getSettings();
    setError(undefined);
    resetPreview();
    setPreviewLoading(true);

    try {
      const response = await cleanData({
        values: selectionInfo.values,
        operations: Array.from(selectedOps),
      });
      setPreviewResult({
        operationSummaries: response.operationSummaries,
        changesCount: response.changesCount,
        cleanedValues: response.cleanedValues,
        warnings: response.warnings,
      });
      addHistoryEntry(
        'cleaning',
        settings.provider,
        settings.model,
        `Nettoyage (${Array.from(selectedOps).join(', ')})`,
        true,
        undefined,
        {
          module: 'cleaning',
          durationMs: Date.now() - startedAt,
          rangeAddress: selectionInfo.address,
        },
      );
    } catch (err) {
      setError((err as Error).message);
      addHistoryEntry(
        'cleaning',
        settings.provider,
        settings.model,
        `Nettoyage (${Array.from(selectedOps).join(', ')})`,
        false,
        (err as Error).message,
        {
          module: 'cleaning',
          durationMs: Date.now() - startedAt,
          rangeAddress: selectionInfo.address,
        },
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  // ─── Step 4: Apply (called by CleaningPreviewCard) ───

  const handleApply = useCallback(async (cleanedValues: unknown[][], strategy: WriteStrategy) => {
    setError(undefined);
    try {
      if (strategy === 'adjacent') {
        await writeValuesAdjacent(cleanedValues);
      } else {
        await writeValues(cleanedValues);
      }
      setAppliedStrategy(strategy);
      setApplied(true);
    } catch (err) {
      setError((err as Error).message);
      throw err; // re-throw so ActionPreviewPanel shows the error
    }
  }, []);

  // ─── Render ─────────────────────────────────────────

  return (
    <div>
      {/* ══ Step 1: Selection ══ */}
      <div className="card">
        <Text className="section-title">1. Lire la selection</Text>
        <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
          Selectionnez des cellules dans Excel contenant les donnees a nettoyer.
        </Text>
        <Button
          size="small"
          appearance="primary"
          icon={<DocumentSearchRegular />}
          onClick={handleReadSelection}
          disabled={selectionLoading}
        >
          Lire la selection
        </Button>

        {selectionLoading && <LoadingSpinner label="Lecture de la selection..." />}

        {selectionInfo && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Badge appearance="outline" size="small" color="informative">
              {selectionInfo.address}
            </Badge>
            <Text size={200} style={{ color: 'var(--text-secondary)' }}>
              {selectionInfo.rowCount} ligne{selectionInfo.rowCount > 1 ? 's' : ''} x{' '}
              {selectionInfo.colCount} colonne{selectionInfo.colCount > 1 ? 's' : ''}
            </Text>
            <Button size="small" appearance="subtle" onClick={resetAll}>
              Reinitialiser
            </Button>
          </div>
        )}
      </div>

      {/* ══ Step 2: Operations (visible after selection) ══ */}
      {selectionInfo && (
        <div className="card">
          <Text className="section-title">2. Choisir les operations</Text>
          <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
            Les operations sont appliquees dans l&apos;ordre d&apos;affichage.
            Les cellules vides (null) sont ignorees. Les valeurs numeriques sont converties en texte.
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {CLEANING_OPERATIONS_META.map((meta) => (
              <div key={meta.key} title={meta.description}>
                <Checkbox
                  label={`${meta.label} (${meta.scope === 'row' ? 'lignes' : 'cellules'})`}
                  checked={selectedOps.has(meta.key)}
                  onChange={() => toggleOp(meta.key)}
                  disabled={previewLoading}
                />
              </div>
            ))}
          </div>

          {/* Operation-specific warnings */}
          {operationWarnings.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
              {operationWarnings.map((w, i) => (
                <MessageBar key={i} intent="warning" style={{ borderRadius: 4 }}>
                  <MessageBarBody>{w}</MessageBarBody>
                </MessageBar>
              ))}
            </div>
          )}

          <Button
            appearance="primary"
            icon={<BroomRegular />}
            onClick={handlePreview}
            disabled={selectedOps.size === 0 || previewLoading}
            style={{ marginTop: 12 }}
            size="small"
          >
            Apercu du nettoyage ({selectedOps.size} operation{selectedOps.size > 1 ? 's' : ''})
          </Button>
        </div>
      )}

      {/* ══ Loading / Error ══ */}
      {previewLoading && <LoadingSpinner label="Nettoyage en cours..." />}
      {error && <ErrorDisplay message={error} onDismiss={() => setError(undefined)} />}

      {/* ══ Step 3: Preview + Apply (unified) ══ */}
      {previewResult && selectionInfo && !applied && (
        <>
          <div className="card">
            <Text className="section-title">Confiance du nettoyage</Text>
            <ConfidenceBadge
              meta={{
                score:
                  (previewResult.warnings.length === 0 ? 85 : 68) -
                  Math.min(18, Math.max(0, previewResult.warnings.length - 1) * 4),
                reasons: [
                  previewResult.changesCount > 0
                    ? `Nettoyage avec ${previewResult.changesCount} changement(s) detecte(s).`
                    : 'Aucun changement detecte: verifier les operations selectionnees.',
                  previewResult.warnings.length > 0
                    ? 'Certaines operations ont emis des avertissements.'
                    : 'Aucun avertissement technique sur ce traitement.',
                ],
              }}
            />
          </div>
          <CleaningPreviewCard
            originalValues={selectionInfo.values}
            cleanedValues={previewResult.cleanedValues}
            operationSummaries={previewResult.operationSummaries}
            changesCount={previewResult.changesCount}
            warnings={previewResult.warnings}
            selectionAddress={selectionInfo.address}
            onApply={handleApply}
            onCancel={resetPreview}
          />
        </>
      )}

      {/* ══ Success ══ */}
      {applied && (
        <MessageBar intent="success" style={{ borderRadius: 4 }}>
          <MessageBarBody style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CheckmarkRegular />
            Donnees nettoyees appliquees avec succes
            {appliedStrategy === 'adjacent' ? ' dans la colonne adjacente' : ' sur place'}.
          </MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
};
